import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, FileText, Lock, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectNoneItem,
  SelectTrigger,
  SelectValue,
  clearable,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EntityCombobox from "@/components/EntityCombobox";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SalesInvoicePrint, {
  type SalesInvoicePrintData,
} from "@/components/SalesInvoicePrint";
import { formatNumber } from "@/lib/format";
import { renderReactToPdf, safePdfFilename } from "@/lib/htmlPdf";
import { renderSalesInvoiceTerms } from "@/lib/salesInvoiceTerms";

interface CompanySettings {
  name?: string;
  address?: string;
  phone?: string;
  email?: string;
  currency_code?: string;
  currency_symbol?: string;
}

const UNITS = ["Pieces", "Sqm", "Lm", "Kg", "Rolls", "Bundles"];

interface EligibleQuote {
  id: number;
  quote_number: string;
  customer_name: string;
  telephone?: string;
  location?: string;
  discount_percent?: number;
}

interface InvoiceItem {
  item_type: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface QuoteFullApi {
  id: number;
  quote_number: string;
  customer_name: string;
  telephone: string;
  location: string;
  discount_percent: number;
  transportation: number;
  installation_percent: number;
  installation_amount: number;
  notes: string;
  items: Array<{
    product_name: string;
    description: string;
    unit: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
}

interface SalesInvoiceFormSupport {
  customers: Array<{
    id: number;
    name: string;
    phone: string;
    address: string;
  }>;
  quotations: EligibleQuote[];
}

interface InvoiceApi {
  id: number;
  invoice_number: string;
  quote_id: number | null;
  customer_id: number | null;
  created_by_name?: string | null;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  quotation_ref: string;
  invoice_date: string | null;
  due_date: string | null;
  status: string;
  subtotal: number;
  discount_percent: number;
  discount_amount: number;
  taxable_amount: number;
  nhil: number;
  getfund: number;
  vat: number;
  transportation: number;
  installation_percent: number;
  installation_amount: number;
  total_amount: number;
  notes: string;
  items: Array<{
    line_no: number;
    item_type: string;
    description: string;
    quantity: number;
    unit_price: number;
    line_total: number;
  }>;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// Pick the installation entry mode that matches how a source quote / saved
// invoice stored installation: a non-zero percent loads in "percent" mode; a
// flat amount with no percent loads in "amount" mode so the figure is treated
// as authoritative and never re-derived (and overwritten) from the subtotal.
function deriveInstallationMode(
  percent: number,
  amount: number,
): "percent" | "amount" {
  if (Number(percent || 0) > 0) return "percent";
  if (Number(amount || 0) > 0) return "amount";
  return "percent";
}

export default function SalesInvoiceForm() {
  const { id } = useParams<{ id: string }>();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { user, can, isFeatureEnabled } = useAuth();
  const taxEnabled = isFeatureEnabled("tax");
  // Creating and editing are independently controllable: sales_invoices.create
  // gates the new-invoice flow, sales_invoices.write gates editing.
  const canCreate = can("sales_invoices.create");
  const canEdit = can("sales_invoices.write");
  const canCancel = can("sales_invoices.cancel");
  const isCancelledPreview = pathname.includes("/sales-invoices/cancelled/");
  const isExisting = Boolean(id) && !isCancelledPreview;
  const isSavedLocked = isExisting;
  const isPreview = isCancelledPreview || isSavedLocked;
  const canSubmit = isExisting ? canEdit : canCreate;
  const readOnly = isPreview || !canSubmit;
  const showSelects = !readOnly;
  const wantsAutoOpenPdf = useMemo(
    () => new URLSearchParams(search).get("openPdf") === "1",
    [search],
  );

  const [eligibleQuotes, setEligibleQuotes] = useState<EligibleQuote[]>([]);
  const [editingQuoteOption, setEditingQuoteOption] =
    useState<EligibleQuote | null>(null);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [quoteId, setQuoteId] = useState<number | null>(null);
  const [quotationRef, setQuotationRef] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayIso);
  const [status, setStatus] = useState("draft");
  const [discountPercent, setDiscountPercent] = useState(0);
  // Optional services carried over from the source quotation. Persisted on
  // the sales invoice so the invoice PDF mirrors the quotation totals
  // line-for-line. installationPercent and installationAmount are kept in sync
  // by the gated effect below: a percentage re-derives the amount, while a
  // fixed amount imported from the quote is preserved verbatim (see
  // installationMode) so it survives into the invoice total and PDF.
  const [transportation, setTransportation] = useState(0);
  const [installationPercent, setInstallationPercent] = useState(0);
  const [installationAmount, setInstallationAmount] = useState(0);
  // Which input drives installation, mirroring QuotationForm: "percent"
  // derives the amount from the subtotal; "amount" treats the figure as a
  // flat amount the recompute effect must never clobber. When an invoice is
  // imported from / derived off a quote (or loaded from a saved row) the mode
  // is set to match how that source stored installation, so a fixed amount
  // (percent 0, amount > 0) survives untouched.
  const [installationMode, setInstallationMode] = useState<
    "percent" | "amount"
  >("percent");
  const [items, setItems] = useState<InvoiceItem[]>([
    {
      item_type: "Pieces",
      description: "",
      quantity: 0,
      unit_price: 0,
      line_total: 0,
    },
  ]);
  const [notes, setNotes] = useState("");
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [cancelMeta, setCancelMeta] = useState<{
    reason: string;
    cancelledBy: string;
    cancelledAt: string;
  } | null>(null);

  const [company, setCompany] = useState<CompanySettings>({});
  const [invoiceTerms, setInvoiceTerms] = useState<string | null>(null);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState("");
  const [pdfDocumentNumber, setPdfDocumentNumber] = useState("");
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [postSaveNavId, setPostSaveNavId] = useState<number | null>(null);
  const [autoOpenConsumed, setAutoOpenConsumed] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + Number(it.line_total || 0), 0),
    [items],
  );
  // Once items originate from a quotation the grid must mirror the source so
  // the downstream invoice stays traceable. Stacks on top of the existing
  // readOnly lock (which already covers saved/cancelled invoices).
  const itemsImportedFromQuote = quoteId != null;
  const itemsLocked = readOnly || itemsImportedFromQuote;
  const showImportLockBanner =
    itemsImportedFromQuote && !isSavedLocked && !isCancelledPreview;
  const discountAmount = subtotal * (discountPercent / 100);
  const taxableAmount = Math.max(0, subtotal - discountAmount);
  // Tax feature OFF -> zero levies so the live total (and the POST payload the
  // server re-validates) excludes tax entirely. Server stays authoritative.
  const nhil = taxEnabled ? taxableAmount * 0.025 : 0;
  const getfund = taxEnabled ? taxableAmount * 0.025 : 0;
  const vat = taxEnabled ? taxableAmount * 0.15 : 0;
  const netTotal = taxableAmount + nhil + getfund + vat;
  const total =
    netTotal + Number(transportation || 0) + Number(installationAmount || 0);

  // Mirror QuotationForm: keep the linked Installation % ↔ Installation
  // amount in sync, but ONLY while in "percent" mode. When a quote was built
  // with a fixed installation amount (installation_percent 0, amount > 0) the
  // invoice loads in "amount" mode and this effect is skipped, so the imported
  // fixed amount is never recomputed to Subtotal × 0% = 0. In "percent" mode a
  // percent or subtotal change re-derives the amount as Subtotal × percent.
  useEffect(() => {
    if (installationMode !== "percent") return;
    const computed = Number(
      (subtotal * (Number(installationPercent || 0) / 100)).toFixed(2),
    );
    if (Math.abs(computed - Number(installationAmount || 0)) > 0.005) {
      setInstallationAmount(computed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationPercent, subtotal, installationMode]);

  function setInstallationPct(next: number) {
    const clamped = Number.isFinite(next) ? Math.max(0, next) : 0;
    setInstallationMode("percent");
    setInstallationPercent(clamped);
    setInstallationAmount(Number((subtotal * (clamped / 100)).toFixed(2)));
  }

  useEffect(() => {
    // Customer + (waybill-issued, no-invoice-yet) quotation pickers come
    // from the Sales-Invoices-scoped form-support endpoint so granting only
    // Sales Invoices is enough — admin does NOT need to also tick
    // Customers / Quotations.
    (async () => {
      try {
        const res = await api.get<SalesInvoiceFormSupport>(
          "/sales-invoices/form-support",
        );
        setEligibleQuotes(res.quotations ?? []);
      } catch (err) {
        toast.error(
          (err as Error)?.message ||
            "Failed to load customer and quotation lists",
        );
      }
    })();
  }, []);

  // Best-effort load of company settings and the sales invoice T&C blob.
  // Mirrors QuotationForm's approach so the PDF picks up the operator's
  // branding and falls back to the bundled DEFAULT_SALES_INVOICE_TERMS when
  // the setting is missing or the user lacks settings.read.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ settings: Record<string, string> }>(
          "/settings",
        );
        const s = res.settings ?? {};
        setCompany({
          name: s["company.name"] || undefined,
          email: s["company.email"] || undefined,
          phone: s["company.phone"] || undefined,
          address: s["company.address"] || undefined,
          currency_code: s["currency.code"] || undefined,
          currency_symbol: s["currency.symbol"] || undefined,
        });
        const rawTerms = s["sales_invoice.terms"];
        setInvoiceTerms(
          typeof rawTerms === "string" && rawTerms.length > 0 ? rawTerms : null,
        );
      } catch {
        // Non-fatal: PDF will still render with default branding and the
        // bundled default Terms & Conditions text.
      }
    })();
  }, []);

  useEffect(() => {
    if (!id) return;

    if (isCancelledPreview) {
      (async () => {
        try {
          const data = await api.get<{
            cancellation: InvoiceApi & {
              cancellation_reason: string;
              cancelled_by: string;
              cancelled_at: string | null;
            };
          }>(`/sales-invoices/cancellations/${id}`);
          const c = data.cancellation;
          setInvoiceNumber(c.invoice_number);
          setQuotationRef(c.quotation_ref);
          setCustomerName(c.customer_name);
          setCustomerPhone(c.customer_phone);
          setCustomerAddress(c.customer_address);
          setInvoiceDate(
            c.invoice_date
              ? new Date(c.invoice_date).toISOString().slice(0, 10)
              : "",
          );
          setStatus(c.status || "draft");
          setDiscountPercent(Number(c.discount_percent || 0));
          setTransportation(Number(c.transportation || 0));
          setInstallationPercent(Number(c.installation_percent || 0));
          setInstallationAmount(Number(c.installation_amount || 0));
          setInstallationMode(
            deriveInstallationMode(
              Number(c.installation_percent || 0),
              Number(c.installation_amount || 0),
            ),
          );
          setNotes(c.notes || "");
          setItems(
            (c.items || []).length
              ? c.items.map((it) => ({
                  item_type: it.item_type || "Pieces",
                  description: it.description,
                  quantity: Number(it.quantity || 0),
                  unit_price: Number(it.unit_price || 0),
                  line_total: Number(it.line_total || 0),
                }))
              : [
                  {
                    item_type: "Pieces",
                    description: "",
                    quantity: 0,
                    unit_price: 0,
                    line_total: 0,
                  },
                ],
          );
          setCancelMeta({
            reason: c.cancellation_reason,
            cancelledBy: c.cancelled_by,
            cancelledAt: c.cancelled_at
              ? new Date(c.cancelled_at).toLocaleString()
              : "",
          });
        } catch (err) {
          toast.error((err as Error).message || "Failed to load cancelled invoice");
        }
      })();
      return;
    }

    (async () => {
      try {
        const data = await api.get<{ sales_invoice: InvoiceApi }>(
          `/sales-invoices/${id}`,
        );
        const inv = data.sales_invoice;
        setInvoiceNumber(inv.invoice_number);
        setQuoteId(inv.quote_id);
        setQuotationRef(inv.quotation_ref);
        if (inv.quote_id) {
          setEditingQuoteOption({
            id: inv.quote_id,
            quote_number: inv.quotation_ref || "",
            customer_name: inv.customer_name,
          });
        }
        setCustomerName(inv.customer_name);
        setCustomerPhone(inv.customer_phone);
        setCustomerAddress(inv.customer_address);
        setInvoiceDate(
          inv.invoice_date
            ? new Date(inv.invoice_date).toISOString().slice(0, 10)
            : todayIso(),
        );
        setStatus(inv.status || "draft");
        setDiscountPercent(Number(inv.discount_percent || 0));
        setTransportation(Number(inv.transportation || 0));
        setInstallationPercent(Number(inv.installation_percent || 0));
        setInstallationAmount(Number(inv.installation_amount || 0));
        setInstallationMode(
          deriveInstallationMode(
            Number(inv.installation_percent || 0),
            Number(inv.installation_amount || 0),
          ),
        );
        setNotes(inv.notes || "");
        setCreatedByName(inv.created_by_name ?? null);
        setItems(
          (inv.items || []).length
            ? inv.items.map((it) => ({
                item_type: it.item_type || "Pieces",
                description: it.description,
                quantity: Number(it.quantity || 0),
                unit_price: Number(it.unit_price || 0),
                line_total: Number(it.line_total || 0),
              }))
            : [
                {
                  item_type: "Pieces",
                  description: "",
                  quantity: 0,
                  unit_price: 0,
                  line_total: 0,
                },
              ],
        );
      } catch (err) {
        toast.error((err as Error).message || "Failed to load invoice");
      }
    })();
  }, [id, isCancelledPreview]);

  const effectiveQuotes = useMemo(() => {
    if (!editingQuoteOption) return eligibleQuotes;
    if (eligibleQuotes.some((q) => q.id === editingQuoteOption.id))
      return eligibleQuotes;
    return [editingQuoteOption, ...eligibleQuotes];
  }, [eligibleQuotes, editingQuoteOption]);

  const customerOptions = useMemo(
    () =>
      Array.from(
        new Set(effectiveQuotes.map((q) => q.customer_name).filter(Boolean)),
      ).sort((a, b) => a.localeCompare(b)),
    [effectiveQuotes],
  );
  const customerQuotes = useMemo(
    () => effectiveQuotes.filter((q) => q.customer_name === customerName),
    [effectiveQuotes, customerName],
  );
  // Display text for the searchable quotation picker: reflects the currently
  // selected quote (after import or initial load) and falls back to the
  // imported quotation_ref so the field never goes blank on reload.
  const selectedQuoteNumber = useMemo(() => {
    if (!quoteId) return "";
    const found = effectiveQuotes.find((q) => q.id === quoteId);
    return found?.quote_number ?? quotationRef ?? "";
  }, [quoteId, effectiveQuotes, quotationRef]);

  async function importQuotation(value: string) {
    const idNum = Number(value);
    if (!idNum) return;
    setQuoteId(idNum);
    try {
      // Sales-invoices-scoped import so users without quotes.read can still
      // pull items off the chosen quote.
      const data = await api.get<{ quotation: QuoteFullApi }>(
        `/sales-invoices/quote-import/${idNum}`,
      );
      const q = data.quotation;
      setQuotationRef(q.quote_number);
      setCustomerName(q.customer_name);
      setCustomerPhone(q.telephone || "");
      setCustomerAddress(q.location || "");
      if (q.discount_percent != null) {
        setDiscountPercent(Number(q.discount_percent));
      }
      setTransportation(Number(q.transportation ?? 0));
      setInstallationPercent(Number(q.installation_percent ?? 0));
      setInstallationAmount(Number(q.installation_amount ?? 0));
      // Carry over installation verbatim from the quote. A fixed amount
      // (percent 0, amount > 0) loads in "amount" mode so the recompute effect
      // leaves it intact; a percentage loads in "percent" mode and re-derives.
      setInstallationMode(
        deriveInstallationMode(
          Number(q.installation_percent ?? 0),
          Number(q.installation_amount ?? 0),
        ),
      );
      const loaded = Array.isArray(q.items) ? q.items : [];
      if (loaded.length) {
        setItems(
          loaded.map((it) => ({
            item_type: it.unit || "Pieces",
            description: it.product_name || it.description,
            quantity: Number(it.quantity || 0),
            unit_price: Number(it.rate || 0),
            line_total: Number(it.amount || 0),
          })),
        );
      }
      toast.success("Imported from quotation");
    } catch (err) {
      toast.error((err as Error).message || "Import failed");
    }
  }

  function updateItem(idx: number, patch: Partial<InvoiceItem>) {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        const next = { ...row, ...patch };
        next.line_total = Number(next.quantity || 0) * Number(next.unit_price || 0);
        return next;
      }),
    );
  }

  function buildPrintDataFromForm(): SalesInvoicePrintData {
    return {
      number: invoiceNumber || "Draft",
      invoiceDate: invoiceDate || null,
      status,
      quotationRef: quotationRef || "",
      customerName,
      customerPhone,
      customerAddress,
      items: items
        .filter((it) => it.description.trim() || it.line_total)
        .map((it) => ({
          description: it.description,
          itemType: it.item_type || "Pieces",
          quantity: Number(it.quantity || 0),
          unitPrice: Number(it.unit_price || 0),
          lineTotal: Number(it.line_total || 0),
        })),
      subtotal,
      discountPercent: Number(discountPercent || 0),
      discountAmount,
      // Hide all tax lines on a freshly-built (unsaved) invoice PDF when the
      // Tax feature is OFF; levies are already zero so the total is tax-free.
      showTax: taxEnabled,
      taxable: taxableAmount,
      nhilRate: 2.5,
      nhil,
      getfundRate: 2.5,
      getfund,
      vatRate: 15,
      vat,
      transportation: Number(transportation || 0),
      installationRate: Number(installationPercent || 0),
      installationAmount: Number(installationAmount || 0),
      netTotal,
      total,
      notes,
    };
  }

  function mapSavedInvoiceToPrint(inv: InvoiceApi): SalesInvoicePrintData {
    return {
      number: inv.invoice_number || "Draft",
      invoiceDate: inv.invoice_date ?? null,
      status: inv.status,
      quotationRef: inv.quotation_ref || "",
      customerName: inv.customer_name,
      customerPhone: inv.customer_phone,
      customerAddress: inv.customer_address,
      items: (inv.items || []).map((it) => ({
        description: it.description,
        itemType: it.item_type || "Pieces",
        quantity: Number(it.quantity || 0),
        unitPrice: Number(it.unit_price || 0),
        lineTotal: Number(it.line_total || 0),
      })),
      subtotal: Number(inv.subtotal || 0),
      discountPercent: Number(inv.discount_percent || 0),
      discountAmount: Number(inv.discount_amount || 0),
      taxable: Number(inv.taxable_amount || 0),
      nhilRate: 2.5,
      nhil: Number(inv.nhil || 0),
      getfundRate: 2.5,
      getfund: Number(inv.getfund || 0),
      vatRate: 15,
      vat: Number(inv.vat || 0),
      transportation: Number(inv.transportation || 0),
      installationRate: Number(inv.installation_percent || 0),
      installationAmount: Number(inv.installation_amount || 0),
      netTotal:
        Number(inv.taxable_amount || 0) +
        Number(inv.nhil || 0) +
        Number(inv.getfund || 0) +
        Number(inv.vat || 0),
      total: Number(inv.total_amount || 0),
      notes: inv.notes || "",
    };
  }

  function resolvePreparedBy(name?: string | null): string {
    const trimmed = name?.trim();
    if (trimmed) return trimmed;
    return (
      user?.name?.trim() ||
      user?.username?.trim() ||
      user?.email?.trim() ||
      "—"
    );
  }

  async function generatePdf(
    printData: SalesInvoicePrintData,
    preparedBy: string,
  ) {
    const printCompany = {
      name: (company.name || "BillForge").toString(),
      address: (company.address || "").toString(),
      phone: (company.phone || "").toString(),
      email: (company.email || "").toString(),
      currency: (company.currency_code || "GHS").toString(),
    };
    const termsText = renderSalesInvoiceTerms(invoiceTerms, printCompany.name);
    return renderReactToPdf(
      <SalesInvoicePrint
        invoice={{ ...printData, terms: termsText }}
        company={printCompany}
        preparedBy={preparedBy}
      />,
      {
        filename: safePdfFilename(
          "SalesInvoice",
          printData.number || "Draft",
        ),
      },
    );
  }

  function openPdfPreview(
    pdf: { url: string; filename: string },
    documentNumber: string,
  ) {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(pdf.url);
    setPdfFilename(pdf.filename);
    setPdfDocumentNumber(documentNumber);
    setPdfModalOpen(true);
  }

  function handlePdfModalOpenChange(open: boolean) {
    if (!open) {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
      setPdfFilename("");
      setPdfDocumentNumber("");
      setPdfModalOpen(false);
      if (postSaveNavId != null) {
        nav(`/sales-invoices/${postSaveNavId}`);
        setPostSaveNavId(null);
      }
    } else {
      setPdfModalOpen(true);
    }
  }

  async function handlePreviewPdfFromForm() {
    if (generatingPdf) return;
    setGeneratingPdf(true);
    try {
      let printData: SalesInvoicePrintData;
      let preparedByName: string | null = createdByName;
      if (isExisting && id) {
        const data = await api.get<{ sales_invoice: InvoiceApi }>(
          `/sales-invoices/${id}`,
        );
        printData = mapSavedInvoiceToPrint(data.sales_invoice);
        preparedByName = data.sales_invoice.created_by_name ?? createdByName;
      } else if (isCancelledPreview && id) {
        const data = await api.get<{
          cancellation: InvoiceApi & {
            cancellation_reason: string;
            cancelled_by: string;
            cancelled_at: string | null;
          };
        }>(`/sales-invoices/cancellations/${id}`);
        printData = mapSavedInvoiceToPrint(data.cancellation);
        preparedByName = data.cancellation.created_by_name ?? createdByName;
      } else {
        printData = buildPrintDataFromForm();
      }
      const pdf = await generatePdf(printData, resolvePreparedBy(preparedByName));
      openPdfPreview(pdf, printData.number);
    } catch (err) {
      toast.error((err as Error).message || "Failed to generate PDF");
    } finally {
      setGeneratingPdf(false);
    }
  }

  // Honour the ?openPdf=1 query param from the listing's "Preview PDF" row
  // action — once the invoice has finished loading, auto-open the preview.
  useEffect(() => {
    if (!wantsAutoOpenPdf || autoOpenConsumed) return;
    if (!id || isCancelledPreview) return;
    if (!invoiceNumber) return;
    setAutoOpenConsumed(true);
    void handlePreviewPdfFromForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsAutoOpenPdf, autoOpenConsumed, id, isCancelledPreview, invoiceNumber]);

  async function confirmCancel() {
    if (!id) return;
    if (!cancelReason.trim()) {
      toast.error("Please enter a reason for cancellation");
      return;
    }
    setCancelling(true);
    try {
      await api.del(`/sales-invoices/${id}`, {
        reason: cancelReason.trim(),
      });
      toast.success("Sales invoice cancelled");
      setCancelOpen(false);
      setCancelReason("");
      nav("/sales-invoices");
    } catch (err) {
      toast.error((err as Error).message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  }

  async function save() {
    if (readOnly) return;
    if (!customerName.trim()) {
      toast.error("Customer name is required");
      return;
    }
    const validItems = items.filter((it) => it.description.trim());
    if (!validItems.length) {
      toast.error("Add at least one line item");
      return;
    }
    setSaving(true);
    try {
      const body = {
        quote_id: quoteId,
        quotation_ref: quotationRef,
        customer_name: customerName.trim(),
        customer_phone: customerPhone,
        customer_address: customerAddress,
        subtotal,
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        taxable_amount: taxableAmount,
        nhil,
        getfund,
        vat,
        transportation: Number(transportation || 0),
        installation_percent: Number(installationPercent || 0),
        installation_amount: Number(installationAmount || 0),
        total_amount: total,
        notes,
        items: validItems,
      };
      const res = await api.post<{ sales_invoice: InvoiceApi }>(
        "/sales-invoices",
        body,
      );
      const saved = res.sales_invoice;
      toast.success("Invoice created");
      setPostSaveNavId(saved.id);
      if (saved.invoice_number) setInvoiceNumber(saved.invoice_number);
      setCreatedByName(saved.created_by_name ?? createdByName);
      const pdf = await generatePdf(
        mapSavedInvoiceToPrint(saved),
        resolvePreparedBy(saved.created_by_name ?? createdByName),
      );
      openPdfPreview(pdf, saved.invoice_number);
    } catch (err) {
      toast.error((err as Error).message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const title = isCancelledPreview
    ? "View Cancelled Invoice"
    : isSavedLocked
      ? "View Sales Invoice"
      : "New Sales Invoice";

  return (
    <div className="space-y-4 max-w-[1300px]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/sales-invoices")}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {Boolean(id) ? (
            <Button
              type="button"
              variant="outline"
              className="h-9 px-3"
              onClick={handlePreviewPdfFromForm}
              disabled={generatingPdf}
            >
              <FileText className="h-4 w-4 mr-1" />
              {generatingPdf ? "Preparing..." : "Preview PDF"}
            </Button>
          ) : null}
          {!readOnly && canSubmit ? (
            <Button
              className="bg-violet-500 hover:bg-violet-400 text-white"
              onClick={save}
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : null}
          {isSavedLocked && canCancel ? (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setCancelReason("");
                setCancelOpen(true);
              }}
            >
              <Ban className="h-4 w-4 mr-2" />
              Cancel Invoice
            </Button>
          ) : null}
        </div>
      </div>

      {isCancelledPreview && cancelMeta ? (
        <p className="text-sm text-rose-700 dark:text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded-lg px-3 py-2">
          Cancelled — {cancelMeta.reason}
          {cancelMeta.cancelledBy
            ? ` (by ${cancelMeta.cancelledBy}${cancelMeta.cancelledAt ? `, ${cancelMeta.cancelledAt}` : ""})`
            : ""}
        </p>
      ) : null}

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="shadow-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {invoiceNumber ? (
              <div className="space-y-1.5">
                <Label>Invoice #</Label>
                <Input
                  value={invoiceNumber}
                  readOnly
                  className="h-10 font-mono"
                />
              </div>
            ) : null}

            {showSelects ? (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Customer Name *</Label>
                  <EntityCombobox
                    aria-label="Customer name"
                    className="h-10"
                    placeholder="Select customer (waybill issued, no invoice yet)"
                    value={customerName}
                    options={customerOptions.map((name) => ({
                      id: name,
                      name,
                    }))}
                    emptyText="No customers with a waybill awaiting a sales invoice."
                    onSelect={(opt) => {
                      setCustomerName(opt.name);
                      setQuoteId(null);
                      setQuotationRef("");
                    }}
                    onClear={() => {
                      setCustomerName("");
                      setQuoteId(null);
                      setQuotationRef("");
                    }}
                  />
                  {customerOptions.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No customers with a waybill awaiting a sales invoice.
                    </p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label>Quotation Number</Label>
                  <EntityCombobox
                    aria-label="Quotation number"
                    className="h-10"
                    value={selectedQuoteNumber}
                    disabled={!customerName}
                    placeholder={
                      customerName
                        ? "Select quotation"
                        : "Select customer first"
                    }
                    options={customerQuotes.map((q) => ({
                      id: q.id,
                      name: q.quote_number,
                      secondary: q.location || q.telephone || null,
                    }))}
                    emptyText="No quotations for this customer."
                    onSelect={(opt) => importQuotation(String(opt.id))}
                    onClear={() => {
                      setQuoteId(null);
                      setQuotationRef("");
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Customer Name</Label>
                    <Input value={customerName} readOnly className="h-10" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Quotation Number</Label>
                    <Input
                      value={quotationRef || "—"}
                      readOnly
                      className="h-10 font-mono"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  readOnly={itemsLocked}
                  disabled={itemsLocked}
                  aria-readonly={itemsLocked ? "true" : undefined}
                  tabIndex={itemsLocked ? -1 : undefined}
                  className={
                    itemsLocked ? "h-10 bg-muted/30 text-foreground" : "h-10"
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label>Address</Label>
                <Input
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  readOnly={itemsLocked}
                  disabled={itemsLocked}
                  aria-readonly={itemsLocked ? "true" : undefined}
                  tabIndex={itemsLocked ? -1 : undefined}
                  className={
                    itemsLocked ? "h-10 bg-muted/30 text-foreground" : "h-10"
                  }
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  readOnly
                  disabled
                  aria-readonly="true"
                  tabIndex={-1}
                  className="h-10 bg-muted/30 text-foreground cursor-not-allowed"
                />
                {!isExisting && !isCancelledPreview ? (
                  <p className="text-xs text-muted-foreground">
                    Stamped automatically on save.
                  </p>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-base">Totals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm pt-0">
            <div className="space-y-1.5">
              <Label>Discount %</Label>
              <Input
                type="number"
                step="0.01"
                value={discountPercent}
                onChange={(e) =>
                  setDiscountPercent(Number(e.target.value || 0))
                }
                readOnly={itemsLocked}
                disabled={itemsLocked}
                aria-readonly={itemsLocked ? "true" : undefined}
                tabIndex={itemsLocked ? -1 : undefined}
                className={
                  itemsLocked ? "h-10 bg-muted/30 text-foreground" : "h-10"
                }
              />
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-mono">{formatNumber(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Discount</span>
              <span className="font-mono">{formatNumber(discountAmount)}</span>
            </div>
            {taxEnabled ? (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Taxable</span>
                  <span className="font-mono">{formatNumber(taxableAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">NHIL (2.5%)</span>
                  <span className="font-mono">{formatNumber(nhil)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GETFund (2.5%)</span>
                  <span className="font-mono">{formatNumber(getfund)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT (15%)</span>
                  <span className="font-mono">{formatNumber(vat)}</span>
                </div>
              </>
            ) : null}
            <div className="flex justify-between border-t pt-2 font-semibold">
              <span>Net Total</span>
              <span className="font-mono">{formatNumber(netTotal)}</span>
            </div>

            <div className="pt-3 mt-2 border-t">
              <p className="text-[11px] tracking-wide uppercase text-muted-foreground mb-2">
                Optional Services
              </p>
              <div className="space-y-2">
                <div className="grid grid-cols-[1fr_120px] items-center gap-2">
                  <Label className="text-xs text-muted-foreground">
                    Transportation
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={transportation}
                    onChange={(e) =>
                      setTransportation(Number(e.target.value || 0))
                    }
                    readOnly={itemsLocked}
                    disabled={itemsLocked}
                    aria-readonly={itemsLocked ? "true" : undefined}
                    tabIndex={itemsLocked ? -1 : undefined}
                    className={
                      itemsLocked
                        ? "h-9 text-right bg-muted/30 text-foreground"
                        : "h-9 text-right"
                    }
                  />
                </div>
                <div className="grid grid-cols-[1fr_120px] items-center gap-2">
                  <Label className="text-xs text-muted-foreground">
                    Installation %
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={installationPercent}
                    onChange={(e) =>
                      setInstallationPct(Number(e.target.value || 0))
                    }
                    readOnly={itemsLocked}
                    disabled={itemsLocked}
                    aria-readonly={itemsLocked ? "true" : undefined}
                    tabIndex={itemsLocked ? -1 : undefined}
                    className={
                      itemsLocked
                        ? "h-9 text-right bg-muted/30 text-foreground"
                        : "h-9 text-right"
                    }
                    title="Installation Amount = Subtotal × this %"
                  />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Installation Amount
                  </span>
                  <span className="font-mono">
                    {formatNumber(installationAmount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-between border-t pt-2 font-semibold text-base">
              <span>Total</span>
              <span className="font-mono text-primary">
                {formatNumber(total)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {showImportLockBanner ? (
        <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
          <Lock className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Items are locked because they were imported from{" "}
            <span className="font-mono">{quotationRef || "the quotation"}</span>
            . To change items, issue a new quotation.
          </span>
        </p>
      ) : null}

      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Line Items</CardTitle>
          {!itemsLocked ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setItems((prev) => [
                  ...prev,
                  {
                    item_type: "Pieces",
                    description: "",
                    quantity: 0,
                    unit_price: 0,
                    line_total: 0,
                  },
                ])
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Line
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="pt-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead className="w-32">Unit</TableHead>
                <TableHead className="w-24 text-right">Qty</TableHead>
                <TableHead className="w-28 text-right">Rate</TableHead>
                <TableHead className="w-28 text-right">Amount</TableHead>
                {!itemsLocked ? <TableHead className="w-12" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Input
                      value={it.description}
                      onChange={(e) =>
                        updateItem(i, { description: e.target.value })
                      }
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                      className={
                        itemsLocked ? "bg-muted/30 text-foreground" : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      value={it.item_type || undefined}
                      onValueChange={clearable((v) =>
                        updateItem(i, { item_type: v }),
                      )}
                      disabled={itemsLocked}
                    >
                      <SelectTrigger
                        className={
                          itemsLocked
                            ? "h-9 bg-muted/30 text-foreground"
                            : "h-9"
                        }
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectNoneItem />
                        {UNITS.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      value={it.quantity}
                      onChange={(e) =>
                        updateItem(i, { quantity: Number(e.target.value || 0) })
                      }
                      className={
                        itemsLocked
                          ? "text-right bg-muted/30 text-foreground"
                          : "text-right"
                      }
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="0.01"
                      value={it.unit_price}
                      onChange={(e) =>
                        updateItem(i, {
                          unit_price: Number(e.target.value || 0),
                        })
                      }
                      className={
                        itemsLocked
                          ? "text-right bg-muted/30 text-foreground"
                          : "text-right"
                      }
                      readOnly={itemsLocked}
                      disabled={itemsLocked}
                      aria-readonly={itemsLocked ? "true" : undefined}
                      tabIndex={itemsLocked ? -1 : undefined}
                    />
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatNumber(it.line_total)}
                  </TableCell>
                  {!itemsLocked ? (
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setItems((prev) =>
                            prev.length <= 1
                              ? prev
                              : prev.filter((_, idx) => idx !== i),
                          )
                        }
                        disabled={items.length <= 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Sales Invoice</DialogTitle>
          </DialogHeader>
          {invoiceNumber ? (
            <p className="text-sm text-muted-foreground">
              Cancelling{" "}
              <span className="font-mono text-foreground">{invoiceNumber}</span>
              {customerName ? ` — ${customerName}` : ""}
            </p>
          ) : null}
          <div className="space-y-1.5">
            <Label>Reason for cancellation *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              placeholder="Enter why this sales invoice is being cancelled..."
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
            >
              Close
            </Button>
            <Button
              onClick={confirmCancel}
              disabled={cancelling}
              variant="destructive"
            >
              {cancelling ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewModal
        open={pdfModalOpen}
        onOpenChange={handlePdfModalOpenChange}
        pdfUrl={pdfPreviewUrl}
        filename={pdfFilename}
        title="Sales Invoice PDF"
        documentNumber={pdfDocumentNumber}
      />
    </div>
  );
}
